import { useState, useRef, useCallback, useEffect } from 'react';
import { SPEECHMATICS_CONFIG } from '../config/api';

/**
 * Hook for real-time speech transcription via backend WebSocket proxy to Speechmatics.
 * The backend handles all Speechmatics authentication and connection management.
 * @param {string} language - Language code (e.g., 'en', 'es', 'fr') for transcription
 */
export const useSpeechmaticsRecorder = (language = 'en') => {
  const [isRecording, setIsRecording] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [error, setError] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [captureSystemAudio, setCaptureSystemAudio] = useState(false);
  const [useExternalAudio, setUseExternalAudio] = useState(true); // Listen to external device via mic - ON by default

  // Convert internal/tech errors into a generic user-facing message
  const sanitizeErrorMessage = useCallback((message) => {
    if (!message) return 'Voice service is temporarily unavailable. Please try again.';
    const lower = message.toLowerCase();
    if (lower.includes('speechmatics') || lower.includes('websocket connection')) {
      return 'Voice service is temporarily unavailable. Please try again.';
    }
    return message;
  }, []);
  
  const TARGET_SAMPLE_RATE = 16000; // Speechmatics expects 16kHz PCM

  const wsRef = useRef(null);
  const audioContextRef = useRef(null);
  const processorRef = useRef(null);
  const streamRef = useRef(null);
  const systemAudioStreamRef = useRef(null);
  const finalTranscriptRef = useRef('');
  const partialTranscriptRef = useRef('');
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const isReadyRef = useRef(false); // True after RecognitionStarted received
  const lastFinalEndTimeRef = useRef(0); // Track end_time of last finalized transcript
  const shouldReconnectRef = useRef(false); // Safe reconnect guard (not based on stale React state)

  /**
   * Downsample Float32Array from sourceSampleRate to TARGET_SAMPLE_RATE (16000).
   * Uses linear interpolation for quality. Returns a new Float32Array at 16kHz.
   */
  const downsampleBuffer = useCallback((buffer, sourceSampleRate) => {
    if (sourceSampleRate === TARGET_SAMPLE_RATE) return buffer;
    if (sourceSampleRate < TARGET_SAMPLE_RATE) {
      // Upsampling not expected, but handle gracefully — just return as-is
      console.warn('⚠️ Source sample rate', sourceSampleRate, 'is below target', TARGET_SAMPLE_RATE);
      return buffer;
    }
    const ratio = sourceSampleRate / TARGET_SAMPLE_RATE;
    const newLength = Math.round(buffer.length / ratio);
    const result = new Float32Array(newLength);
    for (let i = 0; i < newLength; i++) {
      const srcIndex = i * ratio;
      const srcIndexFloor = Math.floor(srcIndex);
      const srcIndexCeil = Math.min(srcIndexFloor + 1, buffer.length - 1);
      const frac = srcIndex - srcIndexFloor;
      result[i] = buffer[srcIndexFloor] * (1 - frac) + buffer[srcIndexCeil] * frac;
    }
    return result;
  }, []);

  /**
   * Convert Float32Array to Int16Array PCM and wrap as Uint8Array for binary send.
   */
  const float32ToPcm16 = useCallback((float32Array) => {
    const pcm = new Int16Array(float32Array.length);
    for (let i = 0; i < float32Array.length; i++) {
      const s = Math.max(-1, Math.min(1, float32Array[i]));
      pcm[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
    }
    return new Uint8Array(pcm.buffer);
  }, []);

  /**
   * Build transcript text from Speechmatics results array.
   * Follows the Speechmatics quickstart pattern: space before words, no space before punctuation.
   * @param {Array} results - Array of result objects from Speechmatics
   * @returns {string} Properly formatted transcript text
   */
  const buildTranscriptFromResults = (results) => {
    if (!Array.isArray(results) || results.length === 0) return '';
    let text = '';
    for (const result of results) {
      const content = result.alternatives?.[0]?.content || result.content || '';
      if (!content) continue;
      // Add space before words (not before punctuation)
      if (result.type === 'word' && text.length > 0) {
        text += ' ';
      }
      text += content;
    }
    return text.trim();
  };

  /**
   * Remove overlapping prefix between the end of `existing` and start of `incoming`.
   * Prevents duplicate words when a partial/final overlaps the tail of finalized text.
   */
  const removeOverlap = (existing, incoming) => {
    if (!existing || !incoming) return incoming;
    const existingWords = existing.split(/\s+/);
    const incomingWords = incoming.split(/\s+/);
    // Check decreasing overlap lengths: does the tail of existing match the head of incoming?
    const maxOverlap = Math.min(existingWords.length, incomingWords.length);
    for (let overlapLen = maxOverlap; overlapLen > 0; overlapLen--) {
      const tail = existingWords.slice(-overlapLen).join(' ').toLowerCase();
      const head = incomingWords.slice(0, overlapLen).join(' ').toLowerCase();
      if (tail === head) {
        // Remove the overlapping head from incoming
        return incomingWords.slice(overlapLen).join(' ');
      }
    }
    return incoming;
  };

  const connect = useCallback(async () => {
    return new Promise(async (resolve, reject) => {
      // Guard against multiple resolve/reject calls from different handlers
      let settled = false;
      let readyTimeout = null;

      const safeResolve = (value) => {
        if (!settled) {
          settled = true;
          if (readyTimeout) clearTimeout(readyTimeout);
          resolve(value);
        }
      };
      const safeReject = (err) => {
        if (!settled) {
          settled = true;
          if (readyTimeout) clearTimeout(readyTimeout);
          reject(err);
        }
      };

      try {
        // Reset ready state
        isReadyRef.current = false;
        lastFinalEndTimeRef.current = 0;
        
        // Connect to backend WebSocket proxy - backend handles Speechmatics connection and auth
        // Read sessionId and email fresh from localStorage at connect time
        // (session may have been created after hook initialization)
        const currentSessionId = localStorage.getItem('current_session_id') || null;
        const currentEmail = localStorage.getItem('userEmail') || null;
        const wsUrl = SPEECHMATICS_CONFIG.getWebSocketUrl(language, currentSessionId, currentEmail);
        console.log('🔌 Connecting to WebSocket:', wsUrl, '| Language:', language, '| SessionId:', currentSessionId);

        const ws = new WebSocket(wsUrl);
        // Ensure binary data is handled as ArrayBuffer
        ws.binaryType = 'arraybuffer';
        
        ws.onopen = () => {
          console.log('✅ WebSocket connected to backend, waiting for RecognitionStarted...');
          setIsConnected(true);
          setError(null);
          // Backend automatically connects to Speechmatics and sends StartRecognition
          // Wait for RecognitionStarted message before resolving
        };

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            console.log('📨 Received message:', data.message || data.type);
            // console.log('📨 Received message:', data.message || data.type, data);
            
            // Handle Speechmatics messages forwarded by backend
            if (data.message === 'RecognitionStarted') {
              console.log('✅ RecognitionStarted received - ready to send audio');
              isReadyRef.current = true;
              safeResolve(ws); // Now we can resolve and start sending audio
              return;
            }
            
            // Info messages (concurrent sessions, quality, etc.)
            if (data.message === 'Info') {
              console.log('ℹ️ Info message:', data);
              return;
            }
            
            if (data.message === 'AudioAdded') {
              // Audio acknowledged by Speechmatics
              return;
            }
            
            if (data.message === 'AddPartialTranscript') {
              const partialStartTime = data.metadata?.start_time ?? 0;
              // Skip stale partials that cover already-finalized audio
              if (partialStartTime < lastFinalEndTimeRef.current && lastFinalEndTimeRef.current > 0) {
                console.log('📝 [PARTIAL] Skipped stale partial (start:', partialStartTime, '< finalEnd:', lastFinalEndTimeRef.current, ')');
                return;
              }
              // Build transcript from results array (Speechmatics recommended), fallback to metadata.transcript
              const partialText = buildTranscriptFromResults(data.results) || data.metadata?.transcript || '';
              // Remove any overlap between finalized text and this partial
              const deduped = removeOverlap(finalTranscriptRef.current, partialText);
              partialTranscriptRef.current = deduped;
              const fullText = deduped
                ? (finalTranscriptRef.current + ' ' + deduped).trim()
                : finalTranscriptRef.current;
              setTranscript(fullText);
              if (partialText) console.log('📝 [PARTIAL]:', partialText, deduped !== partialText ? `(deduped: "${deduped}")` : '');
              
            } else if (data.message === 'AddTranscript') {
              // Build transcript from results array (Speechmatics recommended), fallback to metadata.transcript
              const finalText = buildTranscriptFromResults(data.results) || data.metadata?.transcript || '';
              const endTime = data.metadata?.end_time ?? 0;
              // Update the high-water mark for finalized audio
              if (endTime > lastFinalEndTimeRef.current) {
                lastFinalEndTimeRef.current = endTime;
              }
              // Remove any overlap between existing finalized text and new final
              const deduped = removeOverlap(finalTranscriptRef.current, finalText);
              if (deduped) {
                finalTranscriptRef.current = (finalTranscriptRef.current + ' ' + deduped).trim();
              }
              partialTranscriptRef.current = '';
              setTranscript(finalTranscriptRef.current);
              if (finalText) console.log('📝 [FINAL]:', finalText, deduped !== finalText ? `(deduped: "${deduped}")` : '');
              
            } else if (data.message === 'Error') {
              console.error('❌ Speechmatics error:', data.reason, data);
              setError(sanitizeErrorMessage(data.reason || 'Speechmatics error'));
            } else {
              console.log('📨 Other message:', data.message, data);
            }
          } catch (parseError) {
            console.error('❌ Error parsing WebSocket message:', parseError, 'Raw:', event.data);
          }
        };

        ws.onerror = (err) => {
          console.error('❌ WebSocket error:', err);
          console.error('❌ WebSocket URL attempted:', wsUrl);
          const errorMsg = 'Cannot connect to voice service. Please check your internet connection.';
          setError(sanitizeErrorMessage(errorMsg));
          setIsConnected(false);
          safeReject(new Error(errorMsg));
        };

        ws.onclose = (event) => {
          console.log('🔌 WebSocket closed - code:', event.code, 'reason:', event.reason, 'wasClean:', event.wasClean);
          setIsConnected(false);
          
          // Handle authentication errors
          if (event.code === 1008 || (event.reason && (event.reason.includes('auth') || event.reason.includes('token')))) {
            const authError = 'Authentication failed. Please log in again.';
            setError(authError);
            safeReject(new Error(authError));
            return;
          }
          
          // Handle connection refused / server not available
          if (event.code === 1006) {
            console.error('❌ WebSocket connection failed (code 1006) - server may be unavailable');
            const connectionError = 'Voice service connection failed. The server may be unavailable. Please try again.';
            setError(sanitizeErrorMessage(connectionError));
            safeReject(new Error(connectionError));
            return;
          }
          
          // Auto-reconnect if recording was active (use ref, not stale React state)
          if (shouldReconnectRef.current && reconnectAttemptsRef.current < 5) {
            reconnectAttemptsRef.current += 1;
            reconnectTimeoutRef.current = setTimeout(() => {
              connect().then(safeResolve).catch(safeReject);
            }, 2000 * reconnectAttemptsRef.current); // Exponential backoff
          }
        };

        wsRef.current = ws;
        
        // Set a timeout for RecognitionStarted - if backend doesn't respond in 15 seconds, something is wrong
        readyTimeout = setTimeout(() => {
          if (!isReadyRef.current && ws.readyState === WebSocket.OPEN) {
            console.error('❌ Timeout waiting for RecognitionStarted from backend');
            const timeoutError = 'Voice service initialization timed out. Please try again.';
            setError(sanitizeErrorMessage(timeoutError));
            ws.close();
            safeReject(new Error(timeoutError));
          }
        }, 15000);
        
      } catch (err) {
        console.error('❌ Failed to create WebSocket:', err);
        setError(sanitizeErrorMessage(err.message));
        safeReject(err);
      }
    });
  }, [sanitizeErrorMessage, language]);

  const startRecording = useCallback(async (existingSystemStream = null) => {
    try {
      setError(null);

      // First check if browser supports getUserMedia
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Your browser does not support microphone access. Please use a modern browser like Chrome, Edge, or Firefox.');
      }
      
      // Connect to backend WebSocket first
      shouldReconnectRef.current = true;
      await connect();

      // ── Helper: check if a stream has at least one live audio track ──
      const hasLiveAudio = (stream) => {
        if (!stream || !stream.active) return false;
        return stream.getAudioTracks().some(t => t.readyState === 'live' && !t.ended && t.enabled);
      };

      // ── Helper: log stream details for debugging ──
      const logStreamInfo = (label, stream) => {
        if (!stream) { console.log(`🔍 [${label}] stream is null`); return; }
        console.log(`🔍 [${label}] active:`, stream.active,
          '| audioTracks:', stream.getAudioTracks().map(t => ({
            label: t.label, enabled: t.enabled, muted: t.muted, readyState: t.readyState
          }))
        );
      };

      // ── 1. Get microphone stream — use relaxed constraints (no forced sample rate) ──
      const micConstraints = useExternalAudio ? {
        channelCount: 1,
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: true,
      } : {
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      };

      const micStream = await navigator.mediaDevices.getUserMedia({ audio: micConstraints });
      streamRef.current = micStream;
      logStreamInfo('Microphone', micStream);

      // ── 2. Resolve system audio stream — ONLY accept streams with live audio tracks ──
      let systemStream = null;
      if (hasLiveAudio(existingSystemStream)) {
        systemStream = existingSystemStream;
        console.log('✅ Using passed-in system stream (live audio)');
      } else if (hasLiveAudio(systemAudioStreamRef.current)) {
        systemStream = systemAudioStreamRef.current;
        console.log('✅ Using stored system stream ref (live audio)');
      } else if (captureSystemAudio) {
        // No live stream available — try getDisplayMedia
        try {
          if (navigator.mediaDevices && typeof navigator.mediaDevices.getDisplayMedia === 'function') {
            const displayStream = await navigator.mediaDevices.getDisplayMedia({
              audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
              video: { width: 1, height: 1, frameRate: 1 }
            });
            if (hasLiveAudio(displayStream)) {
              systemStream = displayStream;
              console.log('✅ Got system audio via getDisplayMedia');
            } else {
              console.warn('⚠️ getDisplayMedia returned no live audio tracks');
              setError('No audio captured! Make sure "Also share tab audio" is enabled in the share dialog.');
              // Stop video tracks since we don't need them
              displayStream.getTracks().forEach(t => t.stop());
            }
          } else {
            console.warn('⚠️ getDisplayMedia not supported on this device');
          }
        } catch (err) {
          console.warn('⚠️ System audio capture not available or denied:', err.message);
        }
      }

      if (systemStream) {
        systemAudioStreamRef.current = systemStream;
        logStreamInfo('System Audio', systemStream);
      }

      // ── 3. Create AudioContext at NATIVE sample rate (do NOT force 16000) ──
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioContext;

      // Some browsers create context in suspended state — must resume before capture works
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
      }

      const nativeSampleRate = audioContext.sampleRate;
      console.log('🎵 AudioContext created at native sample rate:', nativeSampleRate, '| Target:', TARGET_SAMPLE_RATE);

      // ── 4. Unified audio pipeline: mic + optional system audio → mix → downsample → send ──
      const hasSystem = !!systemStream;

      if (hasSystem) {
        // Mix mic + system into 2-channel merger
        const merger = audioContext.createChannelMerger(2);
        const micSource = audioContext.createMediaStreamSource(micStream);
        micSource.connect(merger, 0, 0); // mic → left channel

        const systemSource = audioContext.createMediaStreamSource(systemStream);
        systemSource.connect(merger, 0, 1); // system → right channel

        const processor = audioContext.createScriptProcessor(4096, 2, 1);
        processorRef.current = processor;

        processor.onaudioprocess = (e) => {
          if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN || !isReadyRef.current) return;

          const inputL = e.inputBuffer.getChannelData(0); // mic
          const inputR = e.inputBuffer.getChannelData(1); // system
          const length = inputL.length;

          // Mix both channels equally
          const mixed = new Float32Array(length);
          for (let i = 0; i < length; i++) {
            mixed[i] = (inputL[i] + inputR[i]) * 0.5;
          }

          // Downsample from native rate to 16kHz, then convert to PCM16
          const downsampled = downsampleBuffer(mixed, nativeSampleRate);
          wsRef.current.send(float32ToPcm16(downsampled));
        };

        merger.connect(processor);

        // Keep processor alive without playing audio back
        const silence = audioContext.createGain();
        silence.gain.value = 0;
        processor.connect(silence);
        silence.connect(audioContext.destination);

        console.log('🎤🔊 Recording: mic + system audio (mixed)');
      } else {
        // Mic only
        const micSource = audioContext.createMediaStreamSource(micStream);
        const processor = audioContext.createScriptProcessor(4096, 1, 1);
        processorRef.current = processor;

        processor.onaudioprocess = (e) => {
          if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN || !isReadyRef.current) return;

          const input = e.inputBuffer.getChannelData(0);

          // Downsample from native rate to 16kHz, then convert to PCM16
          const downsampled = downsampleBuffer(input, nativeSampleRate);
          wsRef.current.send(float32ToPcm16(downsampled));
        };

        micSource.connect(processor);

        // Keep processor alive without playing audio back
        const silence = audioContext.createGain();
        silence.gain.value = 0;
        processor.connect(silence);
        silence.connect(audioContext.destination);

        console.log('🎤 Recording: mic only', hasSystem ? '(system stream had no live audio)' : '(no system stream)');
      }

      setIsRecording(true);
      reconnectAttemptsRef.current = 0;
      
    } catch (err) {
      console.error('❌ Error in startRecording:', err);
      
      let errorMessage = err.message;
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        errorMessage = 'Microphone access denied. Please allow microphone permissions in your browser settings.';
      } else if (err.name === 'NotFoundError') {
        errorMessage = 'No microphone found. Please connect a microphone and try again.';
      } else if (err.message.includes('token')) {
        errorMessage = 'Authentication error. Please log in again.';
      }
      
      setError(sanitizeErrorMessage(errorMessage));
      stopRecording();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connect, sanitizeErrorMessage, captureSystemAudio, useExternalAudio, downsampleBuffer, float32ToPcm16]);

  const stopRecording = useCallback(() => {
    // Disable reconnection immediately
    shouldReconnectRef.current = false;

    // Clear reconnect timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Stop audio processing
    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    // Stop system audio stream to prevent stale capture
    if (systemAudioStreamRef.current) {
      systemAudioStreamRef.current.getTracks().forEach(track => track.stop());
      systemAudioStreamRef.current = null;
    }

    // Close WebSocket connection (also handle CONNECTING state to prevent hanging)
    if (
      wsRef.current &&
      (wsRef.current.readyState === WebSocket.OPEN ||
       wsRef.current.readyState === WebSocket.CONNECTING)
    ) {
      wsRef.current.close();
    }

    wsRef.current = null;
    isReadyRef.current = false; // Reset ready state
    setIsRecording(false);
    setIsConnected(false);
  }, []);

  const clearTranscript = useCallback(() => {
    console.log('🧹 Clearing transcript - before:', { 
      final: finalTranscriptRef.current, 
      partial: partialTranscriptRef.current 
    });
    setTranscript('');
    finalTranscriptRef.current = '';
    partialTranscriptRef.current = '';
    lastFinalEndTimeRef.current = 0;
    console.log('🧹 Transcript cleared');
  }, []);
  
  // Cleanup on unmount — fully stop everything
  useEffect(() => {
    return () => {
      stopRecording();
    };
  }, [stopRecording]);

  return {
    isRecording,
    transcript,
    error,
    isConnected,
    captureSystemAudio,
    setCaptureSystemAudio,
    useExternalAudio,
    setUseExternalAudio,
    startRecording,
    stopRecording,
    clearTranscript
  };
};
